/**
 * Bemanning mot behov in the Vaktplan (owner's mockup, 2026-09-27): per day the hours planned
 * against the hours the company's staffing need asks for, the wage cost, and the wage share of the
 * day's sales. A day not yet over counts against the manager's forecast, else the average of the
 * last four same weekdays; a day that is over against what it took.
 */
import type { ApiShift } from '@/api/data';
import { oslo, osloISO } from '@/rules';
import type { NeedProfile } from '@/shell/local';
import { addDays, dow } from '@timefly/core';

/** Person-hours the need profile asks for on this date; null when the company set none for the weekday. */
export function needHours(need: NeedProfile, date: string): number | null {
  const day = need[dow(date)];
  if (!day) return null;
  const total = Object.values(day).reduce((a, n) => a + (Number(n) || 0), 0);
  return total > 0 ? total : null;
}

export interface SalesDay {
  actual: number | null;
  forecast: number | null;
  estimate: number | null;
}

/** Which sales figure a day counts against, and where it came from. */
export function salesFor(
  day: SalesDay | undefined,
  past: boolean,
): { value: number | null; from: 'actual' | 'forecast' | 'estimate' | null } {
  if (!day) return { value: null, from: null };
  if (past) return day.actual != null ? { value: day.actual, from: 'actual' } : { value: null, from: null };
  if (day.forecast != null) return { value: day.forecast, from: 'forecast' };
  if (day.estimate != null) return { value: day.estimate, from: 'estimate' };
  return { value: null, from: null };
}

/** Wage cost as a share of sales, in percent; null without sales worth dividing by. */
export const wagePct = (cost: number, sales: number | null) =>
  sales && sales > 0 ? (cost / sales) * 100 : null;

/** "+1,7 t over", "−1,9 t under", "som behovet". */
export function diffText(planned: number, need: number): string {
  const d = Math.round((planned - need) * 10) / 10;
  if (d === 0) return 'som behovet';
  const n = Math.abs(d).toLocaleString('nb-NO', { maximumFractionDigits: 1 });
  return d > 0 ? `+${n} t over` : `−${n} t under`;
}

/** One stretch of the day planned under the need, e.g. 10:30 to 11:00 with 0 of 1 people. */
export interface HourGap {
  /** Oslo clock times; an end at the day's last midnight reads 24:00 */
  from: string;
  to: string;
  /** the fewest people at once in the stretch, and what the need asked there */
  have: number;
  need: number;
}

/**
 * Cover as people at the same time (review F06, retest R04). Every hour the need profile names is
 * cut at each shift start and end and each planned lunch inside it; in every piece the distinct
 * people on an assigned shift and not at lunch are counted. A piece short of the hour's need is
 * short, however full the rest of the hour is: two people 10:00 to 10:30 and nobody 10:30 to
 * 11:00 is not an hour covered. One person on two overlapping shifts counts once; open shifts
 * count nobody; a shift from the evening before counts in the hours it reaches.
 */
/** The assigned shifts as spans with their planned lunch, for counting people at once. */
export function workOf(shifts: ApiShift[]) {
  return shifts
    .filter((s) => s.user_id != null)
    .map((s) => {
      const la = s.planned_lunch_start && s.planned_lunch_minutes ? Date.parse(s.planned_lunch_start) : null;
      return {
        u: s.user_id as number,
        a: Date.parse(s.start_time),
        b: Date.parse(s.end_time),
        la,
        lb: la == null ? null : la + (s.planned_lunch_minutes as number) * 60_000,
      };
    });
}

/** `a` to `b` cut at each shift start and end and each lunch inside it, with the distinct people working in each piece. */
export function pieces(work: ReturnType<typeof workOf>, a: number, b: number) {
  const cuts = new Set([a, b]);
  for (const w of work) for (const t of [w.a, w.b, w.la, w.lb]) if (t != null && t > a && t < b) cuts.add(t);
  const ts = [...cuts].sort((x, y) => x - y);
  const out: { a: number; b: number; have: number }[] = [];
  for (let i = 0; i + 1 < ts.length; i++) {
    const x = ts[i] as number;
    const y = ts[i + 1] as number;
    const m = (x + y) / 2;
    const have = new Set(
      work
        .filter((w) => w.a <= m && m < w.b && !(w.la != null && w.lb != null && w.la <= m && m < w.lb))
        .map((w) => w.u),
    ).size;
    out.push({ a: x, b: y, have });
  }
  return out;
}

export function hourGaps(need: NeedProfile, date: string, shifts: ApiShift[]): HourGap[] {
  const day = need[dow(date)] ?? {};
  const dayEnd = Date.parse(osloISO(addDays(date, 1), '00:00'));
  const work = workOf(shifts);
  const short: { a: number; b: number; have: number; need: number }[] = [];
  for (const h of Object.keys(day)
    .map(Number)
    .sort((x, y) => x - y)) {
    const n = Number(day[h]) || 0;
    if (n <= 0) continue;
    const a = Date.parse(osloISO(date, `${String(h).padStart(2, '0')}:00`));
    const b = Math.min(a + 36e5, dayEnd);
    for (const { a: x, b: y, have } of pieces(work, a, b)) {
      if (have >= n) continue;
      const last = short.at(-1);
      if (last && last.b === x) {
        last.b = y;
        if (have / n < last.have / last.need) {
          last.have = have;
          last.need = n;
        }
      } else short.push({ a: x, b: y, have, need: n });
    }
  }
  const at = (ms: number) => (ms === dayEnd ? '24:00' : (oslo(new Date(ms).toISOString())?.time ?? ''));
  return short.map((g) => ({ from: at(g.a), to: at(g.b), have: g.have, need: g.need }));
}

/** "10:30–11:00" for a gap. */
export const gapText = (g: HourGap) => `${g.from}–${g.to}`;
